import { Component, OnInit } from '@angular/core';
import { BreadcrumbComponent } from "@shared/components/breadcrumb/breadcrumb.component";
import {
  FormsModule, ReactiveFormsModule, UntypedFormBuilder,
  UntypedFormControl, UntypedFormGroup, Validators
} from "@angular/forms";
import { MatButton } from "@angular/material/button";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from "@angular/material/autocomplete";
import { NgClass, NgForOf, NgIf } from "@angular/common";
import { Settlement } from "@core/models/Settlement";
import { debounceTime, distinctUntilChanged, tap } from "rxjs";
import { CatalogsService } from "@core/http/catalogs.service";
import { ActivatedRoute, Router } from "@angular/router";
import { DefaultResponse } from "@core/models/Http/DefaultResponse";
import { BranchService } from "@core/http/table-data-services/Branch.service";
import { UppercaseNoAccentDirective } from "@shared/directives/uppercase-no-accent.directive";
import { HttpMesageHandlerService } from "@core/service/http-mesage-handler";
import { HttpErrorResponse } from "@angular/common/http";
import { REGEX } from "@shared/utils/regex.constants";
import { MsgResponse } from "@core/models/MsgResponse";
import Swal from "sweetalert2";
import { GEMPService } from "@core/http/table-data-services/GEMP.service";
import { BusinessGroup } from "@core/models/BusinessGroup";
import { NgxMatIntlTelInputComponent } from "ngx-mat-intl-tel-input";
import { Branch } from "@core/models/Branch";
import { AuthManagementService } from "@core/service/auth-management.service";
import { DoctorService } from "@core/http/table-data-services/Doctor.service";
import { DoctorDetails } from "@core/models/DoctorDetails";
import { UserService } from "@core/http/table-data-services/User.service";
import { UserDetails } from "@core/models/UserDetails";
import {RegionWordPipe} from "@core/pipes/region-word.pipe";
import {regionWord} from "@shared/utils/region-word.util";

@Component({
  selector: 'app-user-edit-create',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    FormsModule,
    ReactiveFormsModule,
    MatButton,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatOption,
    NgClass,
    MatAutocompleteTrigger,
    MatAutocomplete,
    UppercaseNoAccentDirective,
    NgIf,
    NgForOf,
    NgxMatIntlTelInputComponent,
    RegionWordPipe
  ],
  templateUrl: './branch-edit-create.component.html',
  styleUrl: './branch-edit-create.component.scss',
})
export class BranchEditCreateComponent implements OnInit {
  gid: string | null = null;
  param: string | null = null;

  gemp: BusinessGroup;
  branch: Branch;

  settlements: Settlement[] = [];
  settlementSearch = '';

  branchForm: UntypedFormGroup;
  preferredCountries: string[] = ['mx', 'us', 'es'];

  isParticular = false;

  breadcrumbItems = [
    { label: 'SUCURSAL', link: '/branch/branch-list' },
    { label: 'NUEVA SUCURSAL' }
  ];

  blankObject = {} as Branch;
  blankObjectGEMP = {} as BusinessGroup;
  formControl = new UntypedFormControl('', [Validators.required]);

  constructor(
    private catalogsService: CatalogsService,
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private branchService: BranchService,
    private gempService: GEMPService,
    private httpMsgHandler: HttpMesageHandlerService,
    private authManagement: AuthManagementService,
    private doctorService: DoctorService,
    private userService: UserService,
  ) {
    this.branch = new Branch(this.blankObject);
    this.gemp = { ...this.blankObjectGEMP };

    this.branchForm = this.createBranchForm();

    this.route.paramMap.subscribe(params => {
      const param = params.get('param');
      this.param = param ? atob(param) : null;

      const gid = params.get('gid');
      this.gid = gid ? atob(gid) : null;

      this.setBreadcrumb();
    });
  }

  ngOnInit(): void {
    this.isParticular = this.authManagement.isUserParticular();

    // ✅ si no vino gid por ruta, tomarlo del usuario logueado
    if (!this.gid) {
      const userData = this.authManagement.userData();
      this.gid = userData?.IdGEMP ?? null;
    }

    this.getGEMP();

    if (this.param) {
      this.getBranchDetails(this.param);
    }

    // ✅ cargar datos del particular logueado como responsable sanitario
    if (this.isParticular) {
      this.loadLoggedParticularAsResponsible();
    }

    this.branchForm.get('settlementSearch')?.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap((value: string | Settlement) => {
          const v: any = value;
          if (typeof v === 'string' && v.length === 5 && /^\d+$/.test(v)) {
            this.searchSettlements(v);
          } else if (!v || typeof v !== 'object') {
            this.settlements = [];
          }
        })
      )
      .subscribe();
  }

  /** Imagen del panel izquierdo (logo del GEMP o fallback) */
  get resolvedLeftImage(): string {
    const raw = (this.gemp?.logoBase64 || '').toString().trim();
    const fallback = 'assets/images/empleado.png';
    if (!raw || raw === 'null' || raw === 'undefined') return fallback;
    if (raw.startsWith('data:image/')) return raw;
    try {
      atob(raw);
      return `data:image/png;base64,${raw}`;
    } catch {
      return fallback;
    }
  }

  private setBreadcrumb(): void {
    this.breadcrumbItems = [
      { label: 'SUCURSAL', link: '/branch/branch-list' },
      { label: this.param ? 'EDITAR SUCURSAL' : 'NUEVA SUCURSAL' }
    ];
  }

  getGEMP(): void {
    if (!this.gid) return;

    this.gempService.getById(this.gid).subscribe({
      next: (res: DefaultResponse<BusinessGroup>) => {
        this.gemp = res.data;
      },
      error: (err: HttpErrorResponse) => {
        this.httpMsgHandler.handleMsg(err);
      }
    });
  }

  getBranchDetails(param: string): void {
    this.branchService.getByIdSucursal(param).subscribe({
      next: (res: DefaultResponse<Branch>) => {
        this.settlementSearch = res.data.nombreAsentamiento;
        this.searchSettlements(this.settlementSearch);
        this.branch = this.mapToBranch(res.data);
        this.branchForm = this.createBranchForm();

        // ✅ si es particular, después de cargar sucursal
        // volvemos a imponer sus datos como responsable
        if (this.isParticular) {
          this.applyResponsibleLockState();
          this.loadLoggedParticularAsResponsible();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.httpMsgHandler.handleMsg(err);
      }
    });
  }

  searchSettlements(cp: string): void {
    if (!cp) {
      this.settlements = [];
      return;
    }

    this.catalogsService.getSettlementByPostalCode(cp).subscribe({
      next: (res: DefaultResponse<Settlement[]>) => {
        this.settlements = [...res.data];

        if (this.settlements.length === 0) {
          this.branchForm.patchValue({ settlementSearch: null }, { emitEvent: false });
        }

        const idAsentamientoActual =
          this.branchForm.get('idAsentamiento')?.value || this.branch.idAsentamiento;

        if (idAsentamientoActual) {
          const selected = this.settlements.find(s => s.idAsentamiento === idAsentamientoActual);
          if (selected) {
            this.branchForm.patchValue({
              settlementSearch: selected,
              idAsentamiento: selected.idAsentamiento
            }, { emitEvent: false });
          }
        }
      },
      error: (err) => {
        this.httpMsgHandler.handleMsg(err);
      }
    });
  }

  onSettlementBlur(): void {
    const val = this.branchForm.get('settlementSearch')?.value;
    if (!val || typeof val !== 'object' || !val.idAsentamiento) {
      this.branchForm.patchValue({ settlementSearch: null }, { emitEvent: false });
    }
  }

  createBranchForm(): UntypedFormGroup {
    const form = this.fb.group({
      idGEMP: [this.branch.idGEMP],
      nombre: [this.branch.nombre, [Validators.required, Validators.pattern(REGEX.text)]],
      registroSanitario: [this.branch.registroSanitario, [Validators.required, Validators.pattern(REGEX.registroSanitario)]],
      responsable: [this.branch.responsable, [Validators.required, Validators.pattern(REGEX.text)]],
      cedulaResponsable: [this.branch.cedulaResponsable, [Validators.required, Validators.pattern(REGEX.cedula)]],
      telefonoResponsable: [this.branch.telefonoResponsable, [Validators.required]],
      emailResponsable: [this.branch.emailResponsable, [Validators.required, Validators.pattern(REGEX.correo)]],
      domicilio: [this.branch.domicilio, [Validators.required]],
      idAsentamiento: [this.branch.idAsentamiento],
      settlementSearch: [null, Validators.required]
    });

    if (this.param && this.branch.idSucursal) {
      form.addControl('idSucursal', new UntypedFormControl(this.branch.idSucursal));
    }

    return form;
  }

  mapToBranch(response: any): Branch {
    const branch = new Branch(this.blankObject);

    if (response.idSucursal) branch.idSucursal = response.idSucursal;
    branch.idGEMP = response.idGEMP;
    branch.nombre = response.nombre;
    branch.registroSanitario = response.registroSanitario;
    branch.responsable = response.responsable;
    branch.cedulaResponsable = response.cedulaResponsable;
    branch.telefonoResponsable = response.telefonoResponsable;
    branch.emailResponsable = response.emailResponsable;
    branch.domicilio = response.domicilio;
    branch.idAsentamiento = response.idAsentamiento;

    return branch;
  }

  public confirmAdd(): void {
    const branchData = this.branchForm.getRawValue();
    const selectedSettlement = branchData.settlementSearch as Settlement;

    if (!selectedSettlement || !selectedSettlement.idAsentamiento) {
      Swal.fire({
        icon: 'error',
        title: regionWord('COLONIA REQUERIDA'),
        text: regionWord('DEBES SELECCIONAR UNA COLONIA DE LA LISTA PARA CONTINUAR.')
      }).then();
      return;
    }

    branchData.idAsentamiento = selectedSettlement.idAsentamiento;
    delete branchData.settlementSearch;

    // ✅ idGEMP siempre desde el grupo actual
    branchData.idGEMP = this.gemp.idGEMP || this.gid;

    ['nombre', 'domicilio', 'responsable'].forEach(campo => {
      if (typeof branchData[campo] === 'string') {
        branchData[campo] = branchData[campo].toUpperCase();
      }
    });

    const rawTel = this.branchForm.get('telefonoResponsable')?.value;

    let telE164 = '';
    if (typeof rawTel === 'string') {
      telE164 = rawTel;
    } else if (rawTel?.e164 || rawTel?.e164Number) {
      telE164 = rawTel.e164 || rawTel.e164Number;
    } else if (rawTel?.number) {
      telE164 = rawTel.number;
    }

    branchData.telefonoResponsable = (telE164 || '').trim();

    this.branchService.createUpdateBranch(branchData).subscribe({
      next: (res: DefaultResponse<any>) => {
        const createdIdSucursal =
          res?.data?.idSucursal ||
          res?.data?.IdSucursal ||
          this.extractGuidFromText(res?.data) ||
          this.param ||
          '';

        // Si es particular y estamos creando/actualizando sucursal,
        // relacionar el usuario con la sucursal guardada
        if (this.isParticular && createdIdSucursal) {
          this.linkLoggedParticularToBranchPartial(createdIdSucursal.toString());
          return;
        }

        Swal.fire({
          icon: 'success',
          title: 'SUCURSAL GUARDADA',
          text: 'LA SUCURSAL SE HA GUARDADO CORRECTAMENTE.'
        }).then(() => {
          this.router.navigate(['/branch/branch-list']).then();
        });
      },
      error: (err: HttpErrorResponse | MsgResponse) => {
        this.httpMsgHandler.handleMsg(err);
      }
    });
  }

  private extractGuidFromText(value: unknown): string {
    const text = (value ?? '').toString();

    const match = text.match(
      /\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\b/
    );

    return match?.[0] ?? '';
  }

  displayFn(settlement: Settlement): string {
    return settlement && settlement.asentamiento ? settlement.asentamiento : '';
  }

  setSelectedSettlement(value: Settlement): void {
    this.branchForm.patchValue({
      idAsentamiento: value ? value.idAsentamiento : null
    }, { emitEvent: false });
  }

  // ==========================================================
  // PARTICULAR → RESPONSABLE SANITARIO AUTOMATICO
  // ==========================================================
  private loadLoggedParticularAsResponsible(): void {
    const userData = this.authManagement.userData();
    const idUsuario = (userData?.IdUsuario ?? '').toString().trim();

    if (!idUsuario) {
      return;
    }

    this.doctorService.getDoctorByIdUser(idUsuario).subscribe({
      next: (res: DefaultResponse<DoctorDetails>) => {
        const doctor = res.data as any;
        if (!doctor) return;

        const responsable = this.buildDoctorFullName(doctor);
        const cedula = this.pickFirstString(doctor, [
          'cedula',
          'cedulaProfesional',
          'cedulaResponsable',
          'cedulaGeneral'
        ]);
        const email = this.pickFirstString(doctor, [
          'email',
          'correo',
          'correoElectronico',
          'emailResponsable'
        ]);
        const telefono = this.pickFirstString(doctor, [
          'telefono',
          'movil',
          'celular',
          'telefonoResponsable'
        ]);

        this.branchForm.patchValue({
          responsable,
          cedulaResponsable: cedula,
          emailResponsable: email,
          telefonoResponsable: telefono
        }, { emitEvent: false });

        this.applyResponsibleLockState();
      },
      error: (err: HttpErrorResponse) => {
        this.httpMsgHandler.handleMsg(err);
      }
    });
  }

  private applyResponsibleLockState(): void {
    this.branchForm.get('responsable')?.disable({ emitEvent: false });
    this.branchForm.get('cedulaResponsable')?.disable({ emitEvent: false });
    this.branchForm.get('emailResponsable')?.disable({ emitEvent: false });
    this.branchForm.get('telefonoResponsable')?.disable({ emitEvent: false });
  }

  private buildDoctorFullName(doctor: any): string {
    const explicit =
      this.pickFirstString(doctor, ['nombreCompleto', 'nombres']) || '';

    if (explicit && !doctor?.primerApellido && !doctor?.segundoApellido) {
      return explicit.trim();
    }

    return [
      this.pickFirstString(doctor, ['nombres', 'nombre']),
      this.pickFirstString(doctor, ['primerApellido', 'apellidoPaterno']),
      this.pickFirstString(doctor, ['segundoApellido', 'apellidoMaterno'])
    ]
      .filter(Boolean)
      .join(' ')
      .trim();
  }

  private pickFirstString(source: any, keys: string[]): string {
    for (const key of keys) {
      const value = source?.[key];
      if (value !== null && value !== undefined && `${value}`.trim() !== '') {
        return `${value}`.trim();
      }
    }
    return '';
  }

  private linkLoggedParticularToBranchPartial(idSucursal: string): void {
    const userData = this.authManagement.userData();
    const idUsuario = (userData?.IdUsuario ?? '').toString().trim();

    if (!idUsuario || !idSucursal) {
      return;
    }

    const payload = {
      idUsuario,
      idSucursal
    } as UserDetails;

    this.userService.createUpdateUser(payload).subscribe({
      next: () => {
        Swal.fire({
          icon: 'success',
          title: 'SUCURSAL GUARDADA',
          text: 'LA SUCURSAL SE HA GUARDADO Y SE RELACIONO CORRECTAMENTE CON EL USUARIO.'
        }).then(() => {
          this.router.navigate(['/branch/branch-list']).then();
        });
      },
      error: (err: HttpErrorResponse) => {
        this.httpMsgHandler.handleMsg(err);
      }
    });
  }

  private linkLoggedParticularToBranch(idSucursal: string): void {
    const userData = this.authManagement.userData();
    const usr = (userData?.Usr ?? '').toString().trim();

    if (!usr || !idSucursal) {
      return;
    }

    this.userService.getUserByUsr(usr).subscribe({
      next: (res: DefaultResponse<UserDetails[]>) => {
        const currentUser = (res.data ?? [])[0];
        if (!currentUser?.idUsuario) {
          Swal.fire({
            icon: 'warning',
            title: 'USUARIO NO ENCONTRADO',
            text: 'NO SE PUDO OBTENER EL USUARIO LOGUEADO PARA RELACIONARLO CON LA SUCURSAL.'
          }).then();
          return;
        }

        const payload = this.buildUserUpsertPayloadForBranchRelation(currentUser, idSucursal);

        this.userService.createUpdateUser(payload).subscribe({
          next: () => {
            Swal.fire({
              icon: 'success',
              title: 'SUCURSAL GUARDADA',
              text: 'LA SUCURSAL SE HA GUARDADO Y SE RELACIONO CORRECTAMENTE CON EL USUARIO.'
            }).then(() => {
              this.router.navigate(['/branch/branch-list']).then();
            });
          },
          error: (err: HttpErrorResponse) => {
            this.httpMsgHandler.handleMsg(err);
          }
        });
      },
      error: (err: HttpErrorResponse) => {
        this.httpMsgHandler.handleMsg(err);
      }
    });
  }

  private buildUserUpsertPayloadForBranchRelation(currentUser: any, idSucursal: string): any {
    return {
      idUsuario: currentUser.idUsuario,
      usr: currentUser.usr,
      password: null, // para update normalmente lo mandas null
      idTipoUsuario: currentUser.idTipoUsuario,
      idGEMP: currentUser.idGEMP,
      idSucursal: idSucursal,

      nombres: currentUser.nombres,
      primerApellido: currentUser.primerApellido,
      segundoApellido: currentUser.segundoApellido,

      idAsentamiento: currentUser.idAsentamiento ?? 0,
      domicilio: currentUser.domicilio ?? '',
      movil: currentUser.movil ?? '',
      email: currentUser.email ?? '',

      idColegiado: currentUser.idColegiado ?? 0,

      // 👇 en tu caso parece venir como cedulaGeneral
      cedula:
        currentUser.cedula ??
        currentUser.cedulaGeneral ??
        currentUser.cedulaProfesional ??
        ''
    };
  }
}
